import { useEffect, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";

import { selectCameraDefaults } from "../renderSlice";
import { spacebarPanState } from "../spacebarPanState";

// Pointer position fields used for internal tracking and synthetic event dispatch.
type Pos = {
    clientX: number;
    clientY: number;
    screenX: number;
    screenY: number;
    offsetX: number;
    offsetY: number;
};

// The three phases of the spacebar-pan gesture.
//
// "panning" implies LMB is held — it transitions to "lmb" on Space-up or
// to "idle" on LMB-up. This invariant lets onSpaceKeyUp unconditionally
// restart orbit without a separate lmbHeld check.
//
// "synthetic" = pointerdown was preventDefault()ed (Space-first flow), so the
// browser suppresses compatible mouse events for the entire gesture — all moves
// and the final mouseup must be forwarded as synthetic MouseEvents.
type Phase =
    | { tag: "idle" }
    | { tag: "lmb"; moved: boolean; pos: Pos; synthetic: boolean }
    | { tag: "panning"; pos: Pos; synthetic: boolean };

export function useSpacebarPan({ svg }: { svg: SVGSVGElement | null }) {
    const {
        state: { view, canvas },
    } = useExplorerGlobals();
    const cameraDefaults = useAppSelector(selectCameraDefaults);

    // Refs keep closure values current without re-registering listeners on every render.
    const controllerKindRef = useRef(cameraDefaults.pinhole.controller);
    controllerKindRef.current = cameraDefaults.pinhole.controller;

    const usePointerLockRef = useRef(cameraDefaults.pinhole.usePointerLock);
    usePointerLockRef.current = cameraDefaults.pinhole.usePointerLock;

    // Reset cursor to browser default whenever navigation mode changes.
    useEffect(() => {
        if (!canvas) {
            return;
        }
        canvas.style.cursor = "";
    }, [canvas, cameraDefaults.pinhole.controller]);

    useEffect(() => {
        if (!canvas || !view) {
            return;
        }

        let phase: Phase = { tag: "idle" };

        // ── Helpers ──────────────────────────────────────────────────────────────────

        const mods = (e: KeyboardEvent | PointerEvent) => ({
            shiftKey: e.shiftKey,
            ctrlKey: e.ctrlKey,
            altKey: e.altKey,
        });

        const posOf = (e: PointerEvent): Pos => ({
            clientX: e.clientX,
            clientY: e.clientY,
            screenX: e.screenX,
            screenY: e.screenY,
            offsetX: e.offsetX,
            offsetY: e.offsetY,
        });

        const clearSvgCameraCursor = () => {
            const g = svg?.children.namedItem("camera-cursor");
            if (g) {
                g.innerHTML = "";
            }
        };

        const setNonHoverCursor = (dragging: boolean) => {
            canvas.style.cursor = dragging ? "grabbing" : "move";
            clearSvgCameraCursor();
        };

        // Dispatch a synthetic MouseEvent to the canvas so Novorender processes it through its
        // own camera codepath. Must be MouseEvent (not PointerEvent) — Novorender's ControllerInput
        // only registers mousemove/mousedown/mouseup listeners, not pointermove/pointerdown/pointerup.
        // (Verified in novorender-api.js bundle: addEventListener("mousemove", ...) with no pointer equivalent.)
        //
        // movementX/movementY must be forwarded from the original PointerEvent — the browser only
        // populates these on real events, so they cannot be recomputed from position deltas alone.
        // When e.preventDefault() is called on pointerdown (Space-first flow), the browser suppresses
        // the entire compatible mouse event sequence for that gesture, so every subsequent move and up
        // must also be forwarded as synthetic MouseEvents or Novorender's camera state machine gets stuck.
        const dispatch = (type: string, init: MouseEventInit) => {
            canvas.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, ...init }));
        };

        const isSupportedKind = (kind: string) => kind === "flight" || kind === "cadMiddlePan" || kind === "hover";

        // Skip Space when focus is on a form control or editable element — otherwise
        // typing space in an input would toggle the canvas pan cursor.
        const isTypingTarget = (target: EventTarget | null) => {
            if (!(target instanceof HTMLElement)) {
                return false;
            }
            if (target.isContentEditable) {
                return true;
            }
            const tag = target.tagName;
            return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON";
        };

        // ── Keyboard handlers ────────────────────────────────────────────────────────

        const onSpaceKeyDown = (e: KeyboardEvent) => {
            if (e.code !== "Space" || e.repeat || isTypingTarget(e.target)) {
                return;
            }
            spacebarPanState.held = true;

            const kind = controllerKindRef.current;

            if (phase.tag === "lmb" && phase.moved && !usePointerLockRef.current && isSupportedKind(kind)) {
                // Transition: end the LMB orbit and start a pan immediately.
                const { pos } = phase;
                dispatch("mouseup", {
                    button: 0,
                    buttons: 0,
                    clientX: pos.clientX,
                    clientY: pos.clientY,
                    screenX: pos.screenX,
                    screenY: pos.screenY,
                    ...mods(e),
                });
                spacebarPanState.clickOrigin = { x: pos.offsetX, y: pos.offsetY };
                if (kind !== "hover") {
                    setNonHoverCursor(true);
                }
                dispatch("mousedown", {
                    button: 1,
                    buttons: 4,
                    clientX: pos.clientX,
                    clientY: pos.clientY,
                    screenX: pos.screenX,
                    screenY: pos.screenY,
                    ...mods(e),
                });
                phase = { tag: "panning", pos, synthetic: phase.synthetic };
                return;
            }

            if (kind !== "hover") {
                setNonHoverCursor(false);
            }
        };

        const onSpaceKeyUp = (e: KeyboardEvent) => {
            if (e.code !== "Space" || isTypingTarget(e.target)) {
                return;
            }
            spacebarPanState.held = false;

            if (phase.tag === "panning") {
                // End the pan. LMB is still held (panning invariant) so restart orbit immediately.
                const { pos, synthetic } = phase;
                dispatch("mouseup", {
                    button: 1,
                    buttons: 0,
                    clientX: pos.clientX,
                    clientY: pos.clientY,
                    screenX: pos.screenX,
                    screenY: pos.screenY,
                    ...mods(e),
                });
                spacebarPanState.clickOrigin = null;
                dispatch("mousedown", {
                    button: 0,
                    buttons: 1,
                    clientX: pos.clientX,
                    clientY: pos.clientY,
                    screenX: pos.screenX,
                    screenY: pos.screenY,
                    ...mods(e),
                });
                phase = { tag: "lmb", moved: true, pos, synthetic };
            }

            canvas.style.cursor = "";
            clearSvgCameraCursor();
        };

        // ── Pointer handlers ─────────────────────────────────────────────────────────

        // Intercept spacebar + left-click before Novorender sees it.
        // Suppress the natural mousedown (via preventDefault) then replay it as a
        // synthetic middle-click (buttons=4) so Novorender enters its pan codepath.
        const onPointerDown = (e: PointerEvent) => {
            if (e.target !== canvas || e.button !== 0) {
                return;
            }

            const p = posOf(e);
            const kind = controllerKindRef.current;

            if (spacebarPanState.held && !usePointerLockRef.current && isSupportedKind(kind)) {
                e.stopImmediatePropagation();
                e.preventDefault();
                spacebarPanState.clickOrigin = { x: p.offsetX, y: p.offsetY };
                if (kind !== "hover") {
                    setNonHoverCursor(true);
                }
                dispatch("mousedown", {
                    button: 1,
                    buttons: 4,
                    clientX: p.clientX,
                    clientY: p.clientY,
                    screenX: p.screenX,
                    screenY: p.screenY,
                    ...mods(e),
                });
                phase = { tag: "panning", pos: p, synthetic: true };
            } else {
                phase = { tag: "lmb", moved: false, pos: p, synthetic: false };
            }
        };

        // When LMB was pressed before Space (panning.!synthetic), the browser keeps
        // generating compatible mousemove(buttons=1) events. Discard them — only our
        // synthetic mousemove(buttons=4) should reach Novorender.
        const onMouseMoveDuringPan = (e: MouseEvent) => {
            if (phase.tag === "panning" && e.buttons === 1) {
                e.stopImmediatePropagation();
            }
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!(e.buttons & 1)) {
                return;
            }

            const p = posOf(e);
            const moveInit = {
                movementX: e.movementX,
                movementY: e.movementY,
                clientX: p.clientX,
                clientY: p.clientY,
                screenX: p.screenX,
                screenY: p.screenY,
                ...mods(e),
            };

            switch (phase.tag) {
                case "lmb":
                    phase = { ...phase, pos: p, moved: phase.moved || !!(e.movementX || e.movementY) };
                    if (phase.synthetic) {
                        // pointerdown was prevented — orbit moves must be forwarded synthetically.
                        dispatch("mousemove", { buttons: 1, ...moveInit });
                    }
                    break;
                case "panning":
                    phase = { ...phase, pos: p };
                    dispatch("mousemove", { buttons: 4, ...moveInit });
                    break;
            }
        };

        const onPointerUp = (e: PointerEvent) => {
            if (e.button !== 0) {
                return;
            }

            const endMouseUp = (button: number) =>
                dispatch("mouseup", {
                    button,
                    buttons: 0,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    screenX: e.screenX,
                    screenY: e.screenY,
                    ...mods(e),
                });

            switch (phase.tag) {
                case "lmb": {
                    const { synthetic } = phase;
                    phase = { tag: "idle" };
                    if (synthetic) {
                        endMouseUp(0); // close Novorender's synthetic LMB orbit
                        if (e.target !== canvas) {
                            return; // off-canvas: skip cursor check
                        }
                    } else if (e.target !== canvas) {
                        return;
                    }
                    break;
                }
                case "panning": {
                    spacebarPanState.clickOrigin = null;
                    phase = { tag: "idle" };
                    endMouseUp(1); // close the synthetic MMB pan
                    break;
                }
                default:
                    return;
            }

            // Space still held after gesture ends → show "ready to pan" cursor.
            if (spacebarPanState.held && controllerKindRef.current !== "hover") {
                setNonHoverCursor(false);
            }
        };

        // ── Registration ─────────────────────────────────────────────────────────────

        window.addEventListener("keydown", onSpaceKeyDown, { capture: true });
        window.addEventListener("keyup", onSpaceKeyUp, { capture: true });
        window.addEventListener("mousemove", onMouseMoveDuringPan, { capture: true });
        window.addEventListener("pointerdown", onPointerDown, { capture: true });
        window.addEventListener("pointermove", onPointerMove, { capture: true });
        window.addEventListener("pointerup", onPointerUp, { capture: true });

        return () => {
            window.removeEventListener("keydown", onSpaceKeyDown, { capture: true });
            window.removeEventListener("keyup", onSpaceKeyUp, { capture: true });
            window.removeEventListener("mousemove", onMouseMoveDuringPan, { capture: true });
            window.removeEventListener("pointerdown", onPointerDown, { capture: true });
            window.removeEventListener("pointermove", onPointerMove, { capture: true });
            window.removeEventListener("pointerup", onPointerUp, { capture: true });
            canvas.style.cursor = "";
            spacebarPanState.clickOrigin = null;
        };
    }, [canvas, view, svg]);
}
